import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import {
    Bot as BotIconLucide, ArrowRight, CheckCircle, MessageCircle, Edit3, Zap,
    Lightbulb, Brain as BrainIcon, ShieldCheck, Menu as MenuIcon, X as CloseIcon, Users, ClipboardList
} from 'lucide-react';
import {
    LandingPageWrapper, LandingNav, LandingNavContainer, LandingContainer, MobileNavToggle, LandingNavLinks, NavAppButton, // <-- POPRAWKA: Dodano LandingContainer
    HeroSection, HeroIconWrapper, HeroTitle, HeroSubtitle, HeroButton,
    FeatureSection, SectionTitle, SectionSubtitle, FeatureGrid, ModernFeatureCard,
    LandingFooter
} from './LandingPageStyles';
import { Logo as CommonLogo, StyledButton as CommonStyledButton } from './CommonStyles';
import { useAuth } from '../auth/AuthContext';
import { AppConfig } from '../config';

const getFeatureList = () => {
    const features = [];
    if (AppConfig.features.AI_CHAT) {
        const chatLabel = AppConfig.appName === 'Tarcza Ucznia' ? 'Czat Zgłoszeniowy' : 'Inteligentny Czat AI';
        const chatDesc = AppConfig.appName === 'Tarcza Ucznia' ? 'Zgłoś problem anonimowo lub imiennie do zaufanej osoby.' : 'Prowadź poufne rozmowy z AI, które pomoże Ci zrozumieć Twoje emocje.';
        features.push({ icon: MessageCircle, title: chatLabel, description: chatDesc });
    }
    if (AppConfig.features.JOURNAL) {
        features.push({ icon: Edit3, title: 'Dziennik Refleksji', description: 'Zapisuj myśli, śledź nastrój i odkrywaj wzorce zachowań.' });
    }
    if (AppConfig.features.EXERCISES) {
        features.push({ icon: Zap, title: 'Ćwiczenia Mindfulness', description: 'Techniki relaksacyjne i uważności dla redukcji stresu.' });
    }
    if (AppConfig.features.KNOWLEDGE_BASE) {
        features.push({ icon: Lightbulb, title: 'Baza Wiedzy', description: 'Artykuły o zdrowiu psychicznym i rozwoju osobistym.' });
    }
    if (AppConfig.features.TESTS) {
        features.push({ icon: ClipboardList, title: 'Testy Psychologiczne', description: 'Poznaj siebie lepiej (wyniki zapisywane po zalogowaniu).' });
    }
    if (AppConfig.features.PSYCHOLOGIST_LIST) {
        features.push({ icon: Users, title: 'Lista Specjalistów', description: 'Znajdź profesjonalne wsparcie i umów wizytę online.' });
    }
    features.push({ icon: ShieldCheck, title: 'Prywatność i Bezpieczeństwo', description: 'Twoje dane są szyfrowane i bezpieczne na każdym kroku.' });

    return features;
};


const LandingPage = () => {
    const navigate = useNavigate();
    const { currentUser } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const navRef = useRef(null);
    const mobileMenuButtonRef = useRef(null);

    const featureList = getFeatureList();

    const toggleMobileMenu = () => setIsMobileMenuOpen(prev => !prev);
    const closeMobileMenu = () => setIsMobileMenuOpen(false);

    useEffect(() => {
        const body = document.body;
        if (isMobileMenuOpen) body.classList.add('no-scroll');
        else body.classList.remove('no-scroll');
        return () => body.classList.remove('no-scroll');
    }, [isMobileMenuOpen]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (isMobileMenuOpen && navRef.current && !navRef.current.contains(event.target) &&
                mobileMenuButtonRef.current && !mobileMenuButtonRef.current.contains(event.target)) {
                closeMobileMenu();
            }
        };

        if (isMobileMenuOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isMobileMenuOpen]);


    const handleEnterApp = () => {
        closeMobileMenu();
        navigate(currentUser ? '/app' : '/login');
    };

    const handleNavLinkClick = (pathOrAnchor) => {
        closeMobileMenu();
        if (typeof pathOrAnchor === 'string' && pathOrAnchor.startsWith('#')) {
            const elementId = pathOrAnchor.substring(1);
            const element = document.getElementById(elementId);
            if (element) {
                const navbarHeight = navRef.current ? navRef.current.offsetHeight : 70;
                const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
                const offsetPosition = elementPosition - navbarHeight - 10;

                window.scrollTo({
                    top: offsetPosition,
                    behavior: 'smooth'
                });
            } else {
                console.warn(`Element with id "${elementId}" not found for scrolling.`);
            }
        } else if (typeof pathOrAnchor === 'string') {
            navigate(pathOrAnchor);
        }
    };

    return (
        <LandingPageWrapper className="custom-scrollbar">
            <LandingNav ref={navRef}>
                <LandingNavContainer>
                    <CommonLogo onClick={() => handleNavLinkClick('/')} style={{cursor: 'pointer'}}>
                        <BotIconLucide size={28} />
                        <span style={{fontSize: '1.3rem'}}>{AppConfig.appName}</span>
                    </CommonLogo>

                    <MobileNavToggle onClick={toggleMobileMenu} data-testid="mobile-nav-toggle" ref={mobileMenuButtonRef}>
                        {isMobileMenuOpen ? <CloseIcon /> : <MenuIcon />}
                    </MobileNavToggle>

                    <LandingNavLinks isOpen={isMobileMenuOpen}>
                        <a href="#features" onClick={(e) => { e.preventDefault(); handleNavLinkClick('#features'); }}>Funkcje</a>
                        {currentUser ? (
                            <NavAppButton onClick={() => handleNavLinkClick('/app')} role="button">
                                Przejdź do Aplikacji
                            </NavAppButton>
                        ) : (
                            <>
                                <CommonStyledButton
                                    onClick={() => handleNavLinkClick('/login')}
                                    $variant="outline"
                                    role="button"
                                    style={{ padding: '0.5rem 1rem', fontSize: '0.9rem', minHeight: '40px' }}
                                >
                                    Zaloguj się
                                </CommonStyledButton>
                                {AppConfig.auth.ENABLE_STANDARD_REGISTER && (
                                    <NavAppButton
                                        onClick={() => handleNavLinkClick('/register')}
                                        role="button"
                                        style={{ minHeight: '40px' }}
                                    >
                                        Zarejestruj się
                                    </NavAppButton>
                                )}
                            </>
                        )}
                    </LandingNavLinks>
                </LandingNavContainer>
            </LandingNav>

            <HeroSection>
                <LandingContainer>
                    <HeroIconWrapper>
                        <BrainIcon />
                    </HeroIconWrapper>
                    <HeroTitle>
                        {AppConfig.appName === 'ePsycholog'
                            ? <>Odkryj <span className="highlight">spokój</span> i zrozumienie <span className="highlight">w sobie</span>.</>
                            : <>Twoje <span className="highlight">bezpieczne</span> miejsce, by <span className="highlight">przerwać ciszę</span>.</>
                        }
                    </HeroTitle>
                    <HeroSubtitle>
                        {AppConfig.appName === 'ePsycholog'
                            ? "ePsycholog to Twój osobisty przewodnik, który pomoże Ci nawigować po świecie emocji, zrozumieć swoje myśli i budować zdrowsze nawyki psychiczne."
                            : `${AppConfig.appName} to bezpieczna aplikacja, która pozwala Ci dyskretnie zgłosić problem nękania i uzyskać pomoc, na którą zasługujesz.`
                        }
                        {!currentUser && (
                            <div style={{ marginTop: '1.5rem', fontSize: '1rem' }}>
                                <p>
                                    Korzystaj anonimowo lub{' '}
                                    <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>zaloguj się</a>
                                    {AppConfig.auth.ENABLE_STANDARD_REGISTER &&
                                        <> / <a href="/register" onClick={(e) => { e.preventDefault(); navigate('/register'); }}>zarejestruj się</a></>
                                    }
                                    , aby w pełni spersonalizować swoje doświadczenie.
                                </p>
                            </div>
                        )}
                    </HeroSubtitle>
                    <HeroButton onClick={handleEnterApp}>
                        {currentUser ? 'Otwórz Aplikację' : 'Przejdź do Aplikacji'} <ArrowRight size={20} />
                    </HeroButton>
                </LandingContainer>
            </HeroSection>

            <FeatureSection id="features">
                <LandingContainer>
                    <SectionTitle>Co oferuje {AppConfig.appName}?</SectionTitle>
                    <SectionSubtitle>
                        Narzędzia zaprojektowane, by wspierać Cię każdego dnia.
                    </SectionSubtitle>
                    <FeatureGrid>
                        {featureList.map(feature => (
                            <ModernFeatureCard key={feature.title}>
                                <div className="icon-wrapper"><feature.icon size={28} /></div>
                                <h3>{feature.title}</h3>
                                <p>{feature.description}</p>
                            </ModernFeatureCard>
                        ))}
                    </FeatureGrid>
                </LandingContainer>
            </FeatureSection>

            <LandingFooter>
                <LandingContainer>
                    <CommonLogo className="logo-footer" onClick={() => handleNavLinkClick('/')} style={{cursor: 'pointer', display: 'inline-flex'}}>
                        <BotIconLucide size={28} />
                        <span>{AppConfig.appName}</span>
                    </CommonLogo>
                    <p>Twoje wsparcie w drodze do lepszego samopoczucia.</p>
                    <div className="footer-links">
                        <Link to="/terms-of-service">Regulamin</Link>
                        <a href="mailto:web.epsycholog@gmail.com">Kontakt</a>
                    </div>
                    <p className="copyright">&copy; {new Date().getFullYear()} {AppConfig.appName}. Wszelkie prawa zastrzeżone. Aplikacja nie zastępuje profesjonalnej pomocy.</p>
                </LandingContainer>
            </LandingFooter>
        </LandingPageWrapper>
    );
};

export default LandingPage;